import React, { useState, useRef, useEffect } from 'react';
import { TabsList } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ResponsiveTabsListProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * ResponsiveTabsList - Mobile-friendly scrollable tab navigation
 * 
 * Features:
 * - Horizontal scroll on mobile with scroll-snap
 * - Visible scroll arrow indicators when content overflows
 * - Gradient fade indicators for overflow
 * - Touch-friendly swipe support
 * - Flex-wrap on larger screens
 * 
 * @example
 * <ResponsiveTabsList>
 *   <TabsTrigger value="tab1">Tab 1</TabsTrigger>
 *   <TabsTrigger value="tab2">Tab 2</TabsTrigger>
 * </ResponsiveTabsList>
 */
const ResponsiveTabsList: React.FC<ResponsiveTabsListProps> = ({ 
  children, 
  className 
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeftArrow(scrollLeft > 5);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Initial check - delayed to ensure layout is complete
    updateArrows();
    requestAnimationFrame(updateArrows);
    const timer = setTimeout(updateArrows, 100);

    // Listen for scroll events
    el.addEventListener('scroll', updateArrows, { passive: true });
    
    // Listen for resize
    const resizeObserver = new ResizeObserver(updateArrows);
    resizeObserver.observe(el);

    return () => {
      clearTimeout(timer);
      el.removeEventListener('scroll', updateArrows);
      resizeObserver.disconnect();
    };
  }, []);

  const scrollBy = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    
    const scrollAmount = el.clientWidth * 0.6;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  return (
    <div className="relative w-full">
      {/* Mobile: use wrapping grid so all tabs are visible */}
      <TabsList 
        ref={scrollRef}
        className={cn(
          // Base styles
          "w-full p-1",
          // Mobile: 2-column grid to wrap tabs into rows
          "grid grid-cols-2 gap-1",
          // Desktop (sm+): single row flex
          "sm:flex sm:flex-wrap sm:justify-start sm:gap-1",
          className
        )}
      >
        {children}
      </TabsList>
    </div>
  );
};

export default ResponsiveTabsList;
